import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tabs, Tab, Card, Table, Button, Modal, Form, Alert, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';

/**
 * BillingConfigPage — admin-pflegt drei Billing-Konfig-Bereiche:
 *  1. Rabattcodes (discount_codes)
 *  2. Mindestlaufzeit-Stufen (commitment_term_discounts)
 *  3. Zahlungsmethoden (payment_methods)
 *
 * Alle drei sind so eng verwandt (Bestell-Wizard-Konfig), dass eine
 * einzelne Seite mit Tabs sauberer ist als drei Sidebar-Einträge.
 */
export default function BillingConfigPage() {
  const [tab, setTab] = useState('discount_codes');
  return (
    <>
      <PageHeader
        title="Billing-Konfiguration"
        subtitle="Rabattcodes, Mindestlaufzeit-Stufen und Zahlungsmethoden für den Bestell-Wizard"
        icon="tabler:settings-dollar"
      />
      <Tabs activeKey={tab} onSelect={setTab} className="mb-3" mountOnEnter unmountOnExit>
        <Tab eventKey="discount_codes" title="Rabattcodes">
          <DiscountCodesTab />
        </Tab>
        <Tab eventKey="commitment_terms" title="Mindestlaufzeit / Term-Rabatt">
          <CommitmentTermsTab />
        </Tab>
        <Tab eventKey="payment_methods" title="Zahlungsmethoden">
          <PaymentMethodsTab />
        </Tab>
      </Tabs>
    </>
  );
}

/* ============================================================ *
 * Rabattcodes
 * ============================================================ */

const EMPTY_DISCOUNT = {
  code: '', description: '',
  kind: 'percent', value: 10,
  feature_id: '',
  valid_from: '', valid_until: '',
  max_uses: '', max_uses_per_customer: '',
  is_active: true
};

function DiscountCodesTab() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = auth.can('discount_codes', 'create');

  const codes = useQuery({
    queryKey: ['discount-codes'],
    queryFn: () => api.get('/discount-codes', { params: { include_inactive: true } }).then(r => r.data.items)
  });
  const features = useQuery({
    queryKey: ['features-min'],
    queryFn: () => api.get('/features').then(r => r.data.items || [])
  });

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DISCOUNT);
  const [error, setError] = useState(null);

  const open = (item = null) => {
    if (item) {
      setEditing(item);
      setDraft({
        code: item.code, description: item.description || '',
        kind: item.kind, value: item.value,
        feature_id: item.feature_id || '',
        valid_from: item.valid_from?.slice(0, 16) || '',
        valid_until: item.valid_until?.slice(0, 16) || '',
        max_uses: item.max_uses ?? '',
        max_uses_per_customer: item.max_uses_per_customer ?? '',
        is_active: !!item.is_active
      });
    } else {
      setEditing(null); setDraft(EMPTY_DISCOUNT);
    }
    setError(null); setShowModal(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: draft.code.trim().toUpperCase(),
        description: draft.description || null,
        kind: draft.kind,
        value: parseInt(draft.value, 10),
        feature_id: draft.feature_id || null,
        valid_from: draft.valid_from || null,
        valid_until: draft.valid_until || null,
        max_uses: draft.max_uses === '' ? null : parseInt(draft.max_uses, 10),
        max_uses_per_customer: draft.max_uses_per_customer === '' ? null : parseInt(draft.max_uses_per_customer, 10),
        is_active: !!draft.is_active
      };
      return editing
        ? api.patch(`/discount-codes/${editing.id}`, body)
        : api.post('/discount-codes', body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['discount-codes'] }); setShowModal(false); },
    onError: (err) => setError(err?.response?.data?.message || err.message)
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/discount-codes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discount-codes'] })
  });

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span><strong>Rabattcodes</strong> · {(codes.data || []).length} Eintrag(e)</span>
        {canWrite && (
          <Button size="sm" onClick={() => open()}>
            <Icon icon="tabler:plus" className="me-1" /> Neuer Code
          </Button>
        )}
      </Card.Header>
      <Card.Body className="p-0">
        {codes.isLoading ? <div className="p-3 text-muted">Lädt…</div> : (
          <Table hover responsive className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Code</th><th>Typ</th><th>Wert</th><th>Bindung</th>
                <th>Gültigkeit</th><th>Limit</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {(codes.data || []).map(c => (
                <tr key={c.id}>
                  <td><code className="fw-semibold">{c.code}</code>
                    {c.description && <div className="text-muted small">{c.description}</div>}
                  </td>
                  <td>{c.kind === 'percent' ? 'Prozent' : 'Fix'}</td>
                  <td className="fw-semibold">
                    {c.kind === 'percent' ? `${c.value} %` : `${(c.value / 100).toFixed(2)} EUR`}
                  </td>
                  <td>
                    {c.feature_id
                      ? <Badge bg="info">{features.data?.find(f => f.id === c.feature_id)?.key || 'Feature'}</Badge>
                      : <Badge bg="secondary">Gesamt</Badge>}
                  </td>
                  <td className="small">
                    {c.valid_from?.slice(0, 10) || '—'} → {c.valid_until?.slice(0, 10) || '∞'}
                  </td>
                  <td className="small">
                    {c.max_uses ? `${c.max_uses} ges.` : 'unbegrenzt'}
                    {c.max_uses_per_customer ? ` · ${c.max_uses_per_customer}/Kunde` : ''}
                  </td>
                  <td>
                    <Badge bg={c.is_active ? 'success' : 'secondary'}>
                      {c.is_active ? 'aktiv' : 'inaktiv'}
                    </Badge>
                  </td>
                  <td className="text-end">
                    {canWrite && (
                      <>
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => open(c)}>
                          <Icon icon="tabler:edit" />
                        </Button>
                        <Button size="sm" variant="outline-danger"
                                onClick={() => window.confirm(`Code "${c.code}" wirklich loeschen?`) && remove.mutate(c.id)}>
                          <Icon icon="tabler:trash" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {(codes.data || []).length === 0 && (
                <tr><td colSpan={8} className="text-center text-muted py-4">
                  Noch keine Rabattcodes. {canWrite && 'Klick „Neuer Code" oben rechts.'}
                </td></tr>
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? `Rabattcode bearbeiten: ${editing.code}` : 'Neuer Rabattcode'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="small">{error}</Alert>}
          <Form>
            <div className="row g-3">
              <Form.Group className="col-md-4">
                <Form.Label>Code <span className="text-danger">*</span></Form.Label>
                <Form.Control type="text" value={draft.code}
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                  placeholder="z. B. FRUEHBUCHER25" disabled={!!editing}
                  style={{ textTransform: 'uppercase' }} />
                <Form.Text className="text-muted">3–40 Zeichen, A–Z 0–9 _ −. Nicht aenderbar nach Anlage.</Form.Text>
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Typ</Form.Label>
                <Form.Select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
                  <option value="percent">Prozent</option>
                  <option value="absolute">Fester Abzug (Cent)</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Wert</Form.Label>
                <Form.Control type="number" min="0" max={draft.kind === 'percent' ? 100 : undefined}
                  value={draft.value}
                  onChange={(e) => setDraft({ ...draft, value: e.target.value })} />
                <Form.Text className="text-muted">
                  {draft.kind === 'percent' ? '0–100 %' : 'Cents (z. B. 5000 = 50 EUR)'}
                </Form.Text>
              </Form.Group>

              <Form.Group className="col-12">
                <Form.Label>Beschreibung</Form.Label>
                <Form.Control type="text" value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="z. B. Einfuehrungs-Rabatt 2026" />
              </Form.Group>

              <Form.Group className="col-12">
                <Form.Label>Bindung an Feature (optional)</Form.Label>
                <Form.Select value={draft.feature_id}
                  onChange={(e) => setDraft({ ...draft, feature_id: e.target.value })}>
                  <option value="">— gilt fuer Gesamtbestellung —</option>
                  {(features.data || []).map(f => (
                    <option key={f.id} value={f.id}>{f.key} — {f.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label>Gueltig ab</Form.Label>
                <Form.Control type="datetime-local" value={draft.valid_from}
                  onChange={(e) => setDraft({ ...draft, valid_from: e.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label>Gueltig bis</Form.Label>
                <Form.Control type="datetime-local" value={draft.valid_until}
                  onChange={(e) => setDraft({ ...draft, valid_until: e.target.value })} />
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label>Max. Einloesungen (gesamt)</Form.Label>
                <Form.Control type="number" min="0" value={draft.max_uses}
                  onChange={(e) => setDraft({ ...draft, max_uses: e.target.value })}
                  placeholder="leer = unbegrenzt" />
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label>Max. pro Kunde</Form.Label>
                <Form.Control type="number" min="0" value={draft.max_uses_per_customer}
                  onChange={(e) => setDraft({ ...draft, max_uses_per_customer: e.target.value })}
                  placeholder="leer = unbegrenzt" />
              </Form.Group>

              <Form.Group className="col-12">
                <Form.Check type="switch" id="dc-active" label="aktiv"
                  checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending || !draft.code.trim()}>
            {save.isPending ? 'Speichere…' : (editing ? 'Aktualisieren' : 'Anlegen')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}

/* ============================================================ *
 * Mindestlaufzeit-Stufen
 * ============================================================ */

function CommitmentTermsTab() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = auth.can('commitment_terms', 'create');

  const terms = useQuery({
    queryKey: ['commitment-terms'],
    queryFn: () => api.get('/commitment-terms', { params: { include_inactive: true } }).then(r => r.data.items)
  });

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ term_months: 12, discount_percent: 10, label: '', is_active: true });
  const [error, setError] = useState(null);

  const open = (item = null) => {
    if (item) {
      setEditing(item);
      setDraft({
        term_months: item.term_months, discount_percent: item.discount_percent,
        label: item.label || '', is_active: !!item.is_active
      });
    } else {
      setEditing(null);
      setDraft({ term_months: 12, discount_percent: 10, label: '', is_active: true });
    }
    setError(null); setShowModal(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        term_months: parseInt(draft.term_months, 10),
        discount_percent: parseInt(draft.discount_percent, 10),
        label: draft.label || null,
        is_active: !!draft.is_active
      };
      return editing
        ? api.patch(`/commitment-terms/${editing.id}`, body)
        : api.post('/commitment-terms', body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commitment-terms'] }); setShowModal(false); },
    onError: (err) => setError(err?.response?.data?.message || err.message)
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/commitment-terms/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commitment-terms'] })
  });

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span><strong>Mindestlaufzeit-Stufen</strong> — pro Stufe wird im Bestell-Wizard ein Slider-Stop angezeigt mit zugehörigem Term-Rabatt.</span>
        {canWrite && (
          <Button size="sm" onClick={() => open()}>
            <Icon icon="tabler:plus" className="me-1" /> Neue Stufe
          </Button>
        )}
      </Card.Header>
      <Card.Body className="p-0">
        {terms.isLoading ? <div className="p-3 text-muted">Lädt…</div> : (
          <Table hover responsive className="mb-0 align-middle">
            <thead>
              <tr><th>Monate</th><th>Rabatt</th><th>Label</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {(terms.data || []).map(t => (
                <tr key={t.id}>
                  <td className="fw-semibold">{t.term_months}</td>
                  <td>{t.discount_percent} %</td>
                  <td>{t.label || '—'}</td>
                  <td><Badge bg={t.is_active ? 'success' : 'secondary'}>
                    {t.is_active ? 'aktiv' : 'inaktiv'}
                  </Badge></td>
                  <td className="text-end">
                    {canWrite && (
                      <>
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => open(t)}>
                          <Icon icon="tabler:edit" />
                        </Button>
                        <Button size="sm" variant="outline-danger"
                                onClick={() => window.confirm(`${t.term_months}-Monats-Stufe loeschen?`) && remove.mutate(t.id)}>
                          <Icon icon="tabler:trash" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {(terms.data || []).length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted py-4">
                  Keine Stufen — Bestellung erlaubt nur 1 Monat ohne Rabatt.
                </td></tr>
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? `Stufe bearbeiten: ${editing.term_months} Monate` : 'Neue Laufzeit-Stufe'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="small">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Laufzeit (Monate) <span className="text-danger">*</span></Form.Label>
              <Form.Control type="number" min="1" max="120" value={draft.term_months}
                onChange={(e) => setDraft({ ...draft, term_months: e.target.value })}
                disabled={!!editing} />
              <Form.Text className="text-muted">1–120 Monate. Nicht aenderbar nach Anlage.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Rabatt-Prozent <span className="text-danger">*</span></Form.Label>
              <Form.Control type="number" min="0" max="100" value={draft.discount_percent}
                onChange={(e) => setDraft({ ...draft, discount_percent: e.target.value })} />
              <Form.Text className="text-muted">0–100 %</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Label (optional)</Form.Label>
              <Form.Control type="text" value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder='z. B. "Jahresvertrag"' />
            </Form.Group>
            <Form.Check type="switch" id="ct-active" label="aktiv"
              checked={draft.is_active}
              onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Speichere…' : (editing ? 'Aktualisieren' : 'Anlegen')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}

/* ============================================================ *
 * Zahlungsmethoden
 * ============================================================ */

const KIND_LABELS = {
  sepa_transfer: 'SEPA-Überweisung',
  sepa_direct_debit: 'SEPA-Lastschrift',
  credit_card: 'Kreditkarte',
  paypal: 'PayPal',
  sofort: 'Sofortüberweisung',
  klarna_invoice: 'Klarna (Rechnung)',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  stripe_link: 'Stripe Link',
  crypto: 'Krypto',
  cash: 'Bar',
  on_invoice: 'Auf Rechnung',
  other: 'Andere'
};

const EMPTY_PM = {
  kind: 'sepa_transfer',
  label: 'Bankueberweisung',
  description: '',
  details: {},
  is_active: true,
  is_default: false,
  sort_order: 0
};

function PaymentMethodsTab() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = auth.can('payment_methods', 'create');

  const methods = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => api.get('/payment-methods', { params: { include_inactive: true } }).then(r => r.data.items)
  });

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_PM);
  const [error, setError] = useState(null);

  const open = (item = null) => {
    if (item) {
      setEditing(item);
      setDraft({
        kind: item.kind, label: item.label,
        description: item.description || '',
        details: item.details || {},
        is_active: !!item.is_active, is_default: !!item.is_default,
        sort_order: item.sort_order || 0
      });
    } else { setEditing(null); setDraft(EMPTY_PM); }
    setError(null); setShowModal(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        kind: draft.kind, label: draft.label.trim(),
        description: draft.description || null,
        details: draft.details || {},
        is_active: !!draft.is_active, is_default: !!draft.is_default,
        sort_order: parseInt(draft.sort_order, 10) || 0
      };
      return editing
        ? api.patch(`/payment-methods/${editing.id}`, body)
        : api.post('/payment-methods', body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payment-methods'] }); setShowModal(false); },
    onError: (err) => setError(err?.response?.data?.message || err.message)
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/payment-methods/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-methods'] })
  });

  function setDetail(key, val) {
    setDraft(d => ({ ...d, details: { ...d.details, [key]: val } }));
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span><strong>Zahlungsmethoden</strong> — werden auf der Rechnungs-PDF (Default zuerst) ausgewiesen.</span>
        {canWrite && (
          <Button size="sm" onClick={() => open()}>
            <Icon icon="tabler:plus" className="me-1" /> Neue Methode
          </Button>
        )}
      </Card.Header>
      <Card.Body className="p-0">
        {methods.isLoading ? <div className="p-3 text-muted">Lädt…</div> : (
          <Table hover responsive className="mb-0 align-middle">
            <thead>
              <tr><th>Bezeichnung</th><th>Typ</th><th>Details</th><th>Reihenfolge</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {(methods.data || []).map(m => (
                <tr key={m.id}>
                  <td>
                    <strong>{m.label}</strong>
                    {m.is_default && <Badge bg="primary" className="ms-2">Default</Badge>}
                    {m.description && <div className="text-muted small">{m.description}</div>}
                  </td>
                  <td><Badge bg="secondary">{KIND_LABELS[m.kind] || m.kind}</Badge></td>
                  <td className="small text-muted">
                    {Object.entries(m.details || {}).slice(0, 2).map(([k, v]) =>
                      <div key={k}><code>{k}</code>: {String(v).slice(0, 30)}</div>)}
                  </td>
                  <td>{m.sort_order}</td>
                  <td><Badge bg={m.is_active ? 'success' : 'secondary'}>
                    {m.is_active ? 'aktiv' : 'inaktiv'}
                  </Badge></td>
                  <td className="text-end">
                    {canWrite && (
                      <>
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => open(m)}>
                          <Icon icon="tabler:edit" />
                        </Button>
                        <Button size="sm" variant="outline-danger"
                                onClick={() => window.confirm(`Methode "${m.label}" loeschen?`) && remove.mutate(m.id)}>
                          <Icon icon="tabler:trash" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {(methods.data || []).length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted py-4">
                  Noch keine Zahlungsmethoden — Rechnung zeigt keine. {canWrite && 'Klick „Neue Methode".'}
                </td></tr>
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? `Bearbeiten: ${editing.label}` : 'Neue Zahlungsmethode'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="small">{error}</Alert>}
          <Form>
            <div className="row g-3">
              <Form.Group className="col-md-5">
                <Form.Label>Typ</Form.Label>
                <Form.Select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value, details: {} })}>
                  {Object.entries(KIND_LABELS).map(([k, l]) =>
                    <option key={k} value={k}>{l}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-7">
                <Form.Label>Bezeichnung (kundenseitig)</Form.Label>
                <Form.Control type="text" value={draft.label}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                  placeholder='z. B. "Bankueberweisung Volksbank"' />
              </Form.Group>

              <Form.Group className="col-12">
                <Form.Label>Beschreibung (erscheint auf der Rechnung)</Form.Label>
                <Form.Control as="textarea" rows={2} value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="z. B. Bitte Rechnungsnummer im Verwendungszweck angeben." />
              </Form.Group>

              {/* Kind-spezifische Felder */}
              {draft.kind === 'sepa_transfer' && (
                <>
                  <Form.Group className="col-md-6">
                    <Form.Label>Kontoinhaber</Form.Label>
                    <Form.Control type="text" value={draft.details.account_holder || ''}
                      onChange={(e) => setDetail('account_holder', e.target.value)} />
                  </Form.Group>
                  <Form.Group className="col-md-6">
                    <Form.Label>Bank</Form.Label>
                    <Form.Control type="text" value={draft.details.bank || ''}
                      onChange={(e) => setDetail('bank', e.target.value)} />
                  </Form.Group>
                  <Form.Group className="col-md-8">
                    <Form.Label>IBAN</Form.Label>
                    <Form.Control type="text" value={draft.details.iban || ''}
                      onChange={(e) => setDetail('iban', e.target.value)}
                      placeholder="DE12 3456 7890 1234 5678 90" />
                  </Form.Group>
                  <Form.Group className="col-md-4">
                    <Form.Label>BIC</Form.Label>
                    <Form.Control type="text" value={draft.details.bic || ''}
                      onChange={(e) => setDetail('bic', e.target.value)} />
                  </Form.Group>
                </>
              )}
              {draft.kind === 'sepa_direct_debit' && (
                <Form.Group className="col-md-6">
                  <Form.Label>Glaeubiger-ID</Form.Label>
                  <Form.Control type="text" value={draft.details.creditor_id || ''}
                    onChange={(e) => setDetail('creditor_id', e.target.value)}
                    placeholder="DE98ZZZ09999999999" />
                </Form.Group>
              )}
              {draft.kind === 'paypal' && (
                <Form.Group className="col-md-8">
                  <Form.Label>PayPal-Konto (Email)</Form.Label>
                  <Form.Control type="email" value={draft.details.account_email || ''}
                    onChange={(e) => setDetail('account_email', e.target.value)} />
                </Form.Group>
              )}
              {draft.kind === 'crypto' && (
                <>
                  <Form.Group className="col-md-3">
                    <Form.Label>Coin</Form.Label>
                    <Form.Select value={draft.details.coin || 'btc'}
                      onChange={(e) => setDetail('coin', e.target.value)}>
                      <option value="btc">BTC</option>
                      <option value="eth">ETH</option>
                      <option value="usdt">USDT</option>
                      <option value="other">Andere</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="col-md-9">
                    <Form.Label>Wallet-Adresse</Form.Label>
                    <Form.Control type="text" value={draft.details.address || ''}
                      onChange={(e) => setDetail('address', e.target.value)} />
                  </Form.Group>
                </>
              )}
              {draft.kind === 'credit_card' && (
                <Form.Group className="col-12">
                  <Form.Label>Provider</Form.Label>
                  <Form.Control type="text" value={draft.details.provider || ''}
                    onChange={(e) => setDetail('provider', e.target.value)}
                    placeholder="z. B. Stripe, Adyen" />
                </Form.Group>
              )}
              {(draft.kind === 'other' || draft.kind === 'on_invoice' || draft.kind === 'cash') && (
                <Form.Group className="col-12">
                  <Form.Label>Anweisungen / Hinweise</Form.Label>
                  <Form.Control type="text" value={draft.details.instructions || ''}
                    onChange={(e) => setDetail('instructions', e.target.value)} />
                </Form.Group>
              )}

              <Form.Group className="col-md-3">
                <Form.Label>Reihenfolge</Form.Label>
                <Form.Control type="number" value={draft.sort_order}
                  onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-9 d-flex align-items-end gap-3">
                <Form.Check type="switch" id="pm-active" label="aktiv"
                  checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
                <Form.Check type="switch" id="pm-default" label="Default"
                  checked={draft.is_default}
                  onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })} />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={() => save.mutate()}
                  disabled={save.isPending || !draft.label.trim()}>
            {save.isPending ? 'Speichere…' : (editing ? 'Aktualisieren' : 'Anlegen')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
